import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useFees } from '../context/FeeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentCheckout = ({ user, onLogout }) => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getBookingPricing } = useFees();
  
  const [booking, setBooking] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('gcash');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bankProof, setBankProof] = useState(null);
  const [uploadingProof, setUploadingProof] = useState(false);
  const [existingPayment, setExistingPayment] = useState(null);
  const [approvedAddons, setApprovedAddons] = useState([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const fetchData = async () => {
    try {
      const [bookingRes, configRes, paymentRes, chargesRes] = await Promise.all([
        axios.get(`${API}/bookings/${bookingId}`),
        axios.get(`${API}/payments/config`),
        axios.get(`${API}/payments/booking/${bookingId}`).catch(() => ({ data: { has_payment: false } })),
        axios.get(`${API}/bookings/${bookingId}/charges`).catch(() => ({ data: { charges: [] } }))
      ]);
      
      setBooking(bookingRes.data);
      setPaymentConfig(configRes.data);
      setApprovedAddons((chargesRes.data.charges || []).filter(c => c.kind === 'addon' && c.status === 'approved'));
      
      // Set default selected method to first enabled method
      if (configRes.data.supported_methods && configRes.data.supported_methods.length > 0) {
        setSelectedMethod(configRes.data.supported_methods[0]);
      }
      
      if (paymentRes.data.has_payment) {
        setExistingPayment(paymentRes.data.payment);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load booking details');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    if (!booking) return { serviceAmount: 0, clientFee: 0, clientTotal: 0, providerFee: 0, providerPayout: 0 };

    // Unpaid bookings (the only state reaching checkout) are priced at the CURRENT
    // live platform fee via getBookingPricing, so Owner-Panel fee changes apply.
    const p = getBookingPricing(booking);
    return {
      serviceAmount: p.serviceAmount.toFixed(2),
      clientFee: p.clientFee.toFixed(2),
      clientFeeRate: parseFloat(Number(p.clientFeeRate).toFixed(2)).toString(),
      clientTotal: p.clientTotal.toFixed(2),
      providerFee: p.providerFee.toFixed(2),
      providerFeeRate: parseFloat(Number(p.providerFeeRate).toFixed(2)).toString(),
      providerPayout: p.providerPayout.toFixed(2)
    };
  };

  const handlePayment = async () => {
    setProcessing(true);
    setError('');
    
    try {
      const totals = calculateTotal();
      // Client pays the client_total (service amount + client fee)
      const response = await axios.post(`${API}/payments/create`, {
        booking_id: bookingId,
        amount: parseFloat(totals.clientTotal),
        payment_method: selectedMethod,
        redirect_url_success: `${window.location.origin}/payment/success?booking=${bookingId}`,
        redirect_url_failed: `${window.location.origin}/payment/failed?booking=${bookingId}`
      });
      
      const result = response.data;
      
      if (result.checkout_url || result.payment_url) {
        // Redirect to PayMongo checkout for e-wallets
        window.location.href = result.checkout_url || result.payment_url;
      } else if (result.status === 'captured' || result.payment_mode === 'sandbox') {
        // Payment successful (sandbox mode or instant capture)
        setSuccess('Payment successful! Redirecting to dashboard...');
        setTimeout(() => navigate('/dashboard'), 2000);
      } else if (result.status === 'awaiting_verification' || result.status === 'awaiting_transfer') {
        // Bank transfer - show instructions
        setExistingPayment({
          payment_id: result.payment_id,
          status: result.status,
          payment_method: 'bank_transfer'
        });
        setSuccess(result.message || 'Bank transfer initiated. Please upload proof of payment.');
      } else if (result.status === 'awaiting_payment') {
        // Card payment - need to complete on frontend
        setExistingPayment({
          payment_id: result.payment_id,
          status: result.status,
          payment_method: selectedMethod,
          client_key: result.client_key
        });
        setSuccess('Payment initiated. Please complete the payment.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleProofUpload = async (e) => {
    e.preventDefault();
    if (!bankProof || !existingPayment?.payment_id) return;
    
    setUploadingProof(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('proof_image', bankProof);
      
      await axios.post(
        `${API}/payments/${existingPayment.payment_id}/upload-proof`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setSuccess('Proof uploaded successfully! Our team will verify your payment within 24 hours.');
      setExistingPayment({ ...existingPayment, status: 'pending_verification' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload proof');
    } finally {
      setUploadingProof(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Booking Not Found</h2>
            <button onClick={() => navigate('/dashboard')} className="text-blue-600 hover:underline">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totals = calculateTotal();
  const addonsTotal = approvedAddons.reduce((s, a) => s + Number(a.amount || 0), 0);
  const grandTotal = parseFloat(totals.clientTotal) + addonsTotal;
  const grandTotalStr = grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  
  // Define all payment methods
  const allPaymentMethods = [
    { id: 'gcash', name: 'GCash', icon: '📱', color: 'bg-blue-500' },
    { id: 'qrph', name: 'QR Ph', icon: '🔲', color: 'bg-red-600' },
    { id: 'maya', name: 'Maya', icon: '💳', color: 'bg-green-500' },
    { id: 'card', name: 'Credit/Debit Card', icon: '💳', color: 'bg-purple-500' },
    { id: 'bank_transfer', name: 'Bank Transfer', icon: '🏦', color: 'bg-gray-600' }
  ];
  
  // Filter to only enabled payment methods
  const paymentMethods = allPaymentMethods.filter(method => 
    paymentConfig?.supported_methods?.includes(method.id)
  );

  // If no methods enabled, show all (fallback)
  const availableMethods = paymentMethods.length > 0 ? paymentMethods : allPaymentMethods;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="payment-checkout-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-4 bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
          Payment Checkout
        </h1>

        {paymentConfig?.sandbox_mode && (
          <div className="mb-6 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-center gap-2" data-testid="sandbox-indicator">
            <span className="px-2 py-0.5 bg-amber-400 text-amber-900 text-xs font-bold rounded">SANDBOX</span>
            <span className="text-amber-700 text-sm">Payments are simulated. No real charges will be made.</span>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg" data-testid="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg" data-testid="success-message">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4">Order Summary</h2>
            
            <div className="border-b pb-4 mb-4">
              <h3 className="font-semibold text-lg">{booking.service_title}</h3>
              <p className="text-gray-600 text-sm mt-1">Provider: {booking.provider_name}</p>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Service Amount</span>
                <span className="font-semibold">₱{parseFloat(totals.serviceAmount).toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm text-blue-600">
                <span>+ Platform Fee ({totals.clientFeeRate}%)</span>
                <span>₱{parseFloat(totals.clientFee).toLocaleString()}</span>
              </div>
              {approvedAddons.length > 0 && (
                <div className="border-t pt-3 mt-1" data-testid="bundled-addons">
                  <p className="text-sm text-gray-500 mb-2">Approved extra charges:</p>
                  {approvedAddons.map((a) => (
                    <div key={a.id} className="flex justify-between text-sm text-gray-700" data-testid={`bundled-addon-${a.id}`}>
                      <span className="truncate pr-2">{a.description || 'Additional charge'}</span>
                      <span>₱{Number(a.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="border-t pt-3">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total to Pay</span>
                  <span className="text-green-600" data-testid="checkout-total">₱{grandTotalStr}</span>
                </div>
              </div>
            </div>

            {/* Escrow Notice */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-800 flex items-center gap-2">
                🔒 Escrow Protection
              </h4>
              <p className="text-sm text-blue-700 mt-1">
                Your payment is held securely until the job is completed and you confirm satisfaction.
              </p>
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            {existingPayment && ['captured', 'awaiting_transfer', 'awaiting_verification', 'pending_verification'].includes(existingPayment.status) ? (
              // Show existing payment status
              <div>
                <h2 className="text-xl font-bold mb-4">Payment Status</h2>
                
                <div className={`p-4 rounded-lg mb-6 ${
                  existingPayment.status === 'captured' ? 'bg-green-100 text-green-800' :
                  existingPayment.status === 'pending_verification' ? 'bg-yellow-100 text-yellow-800' :
                  existingPayment.status === 'awaiting_transfer' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  <p className="font-semibold">
                    Status: {existingPayment.status.replace(/_/g, ' ').toUpperCase()}
                  </p>
                </div>

                {/* Bank Transfer Instructions */}
                {existingPayment.payment_method === 'bank_transfer' && 
                 ['awaiting_transfer', 'pending_verification'].includes(existingPayment.status) && (
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-100 rounded-lg">
                      <h3 className="font-bold mb-2">Bank Transfer Instructions</h3>
                      <div className="space-y-2 text-sm">
                        <p><span className="text-gray-500">Bank:</span> <strong>{paymentConfig?.bank_details?.bank_name}</strong></p>
                        <p><span className="text-gray-500">Account Name:</span> <strong>{paymentConfig?.bank_details?.account_name}</strong></p>
                        <p><span className="text-gray-500">Account Number:</span> <strong>{paymentConfig?.bank_details?.account_number}</strong></p>
                        <p><span className="text-gray-500">Amount:</span> <strong>₱{grandTotalStr}</strong></p>
                      </div>
                    </div>

                    {existingPayment.status === 'awaiting_transfer' && (
                      <form onSubmit={handleProofUpload} className="mt-4">
                        <label className="block text-gray-700 font-bold mb-2">
                          Upload Proof of Payment
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setBankProof(e.target.files[0])}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          data-testid="proof-upload-input"
                        />
                        <button
                          type="submit"
                          disabled={!bankProof || uploadingProof}
                          className="mt-4 w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
                          data-testid="upload-proof-btn"
                        >
                          {uploadingProof ? 'Uploading...' : 'Upload Proof'}
                        </button>
                      </form>
                    )}

                    {existingPayment.status === 'pending_verification' && (
                      <p className="text-center text-gray-600">
                        ⏳ Your payment proof is being verified. We'll notify you once approved.
                      </p>
                    )}
                  </div>
                )}

                {existingPayment.status === 'captured' && (
                  <div className="text-center">
                    <p className="text-green-600 font-semibold mb-4">✓ Payment Complete</p>
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-semibold"
                    >
                      Go to Dashboard
                    </button>
                  </div>
                )}
              </div>
            ) : (
              // Payment method selection
              <div>
                <h2 className="text-xl font-bold mb-4">Select Payment Method</h2>

                <div className="space-y-3 mb-6">
                  {availableMethods.map((method) => (
                    <button
                      key={method.id}
                      onClick={() => setSelectedMethod(method.id)}
                      className={`w-full p-4 border-2 rounded-lg flex items-center gap-4 transition ${
                        selectedMethod === method.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`payment-method-${method.id}`}
                    >
                      <span className="text-2xl">{method.icon}</span>
                      <span className="font-semibold">{method.name}</span>
                      {selectedMethod === method.id && (
                        <span className="ml-auto text-blue-500">✓</span>
                      )}
                    </button>
                  ))}
                </div>

                {/* PayMongo sandbox notice */}
                {!paymentConfig?.paymongo_configured && selectedMethod !== 'bank_transfer' && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                    ⚠️ Payment gateway is in sandbox mode. Payments will be simulated.
                  </div>
                )}

                <button
                  onClick={handlePayment}
                  disabled={processing}
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-lg hover:shadow-lg font-bold text-lg disabled:opacity-50"
                  data-testid="pay-now-btn"
                >
                  {processing ? 'Processing...' : `Pay ₱${grandTotalStr}`}
                </button>

                <p className="text-center text-sm text-gray-500 mt-4">
                  Secured by PayMongo 🔒
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PaymentCheckout;
